import { Injectable, inject } from '@angular/core';
import { LanguageService } from 'src/app/core/services/language.service';
import { AudioService } from './audio.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
	providedIn: 'root',
})
export class TextToSpeechService {
	// #region Services
	private languageService = inject(LanguageService);
	private audioService = inject(AudioService);
	private trans = inject(TranslateService);
	// #endregion

	private synth: SpeechSynthesis = window.speechSynthesis;
	private textToSpeech: string = '';
	private lang: string;
	private queue: string[] = [];
	private isSpeaking: boolean = false;

	constructor() {
		this.lang = this.languageService.getLanguage();
	}

	public speak(text: string, forceAudio: boolean = false): void {
		if ((this.textToSpeech === text && !forceAudio) || !text || this.queue.includes(text)) {
			return;
		}
		this.textToSpeech = text;
		const usePreloaded = this.audioService.hasAudio(text);
		if (usePreloaded) {
			this.audioService.playAudio(text);
			return;
		}
		if (!this.synth) {
			console.error('Text-to-Speech no es soportado en este navegador.');
			return;
		}
		this.queue.push(text);
		this.processQueue();
	}

	private processQueue() {
		if (this.isSpeaking || !this.queue.length) {
			return;
		}
		const text = this.queue.shift();
		const utterance = new SpeechSynthesisUtterance(this.trans.instant(text));
		const voices: SpeechSynthesisVoice = this.getVoice();
		if (voices) {
			utterance.voice = voices;
			utterance.lang = voices.lang;
		}
		this.isSpeaking = true;
		utterance.onend = () => {
			this.isSpeaking = false;
			this.processQueue();
		};
		utterance.onerror = () => {
			this.isSpeaking = false;
			this.processQueue();
		};
		this.synth.speak(utterance);
	}

	/**
	 * Detiene la síntesis de voz si está en curso, cancela cualquier discurso pendiente,
	 * vacía la cola de textos y actualiza el estado interno a no hablando.
	 */
	public stop() {
		if (this.synth?.speaking) {
			this.synth.cancel();
			this.queue = [];
			this.isSpeaking = false;
		}
		this.audioService.stop();
	}

	public isTTSAvailable(): boolean {
		return Boolean(this.synth);
	}

	public getAvailableVoices(): SpeechSynthesisVoice[] {
		return this.synth
			.getVoices()
			.filter((voice: SpeechSynthesisVoice) => voice.localService && voice.lang.includes(this.lang));
	}

	private getVoice(): SpeechSynthesisVoice {
		const availableVoices = this.getAvailableVoices();
		// Default voice
		const foundDefaultVoice: SpeechSynthesisVoice = availableVoices.find(
			(voice: SpeechSynthesisVoice) => voice.name.includes('Helena') || voice.voiceURI.includes('Helena'),
		);
		if (foundDefaultVoice) {
			return foundDefaultVoice;
		}
		if (availableVoices.length) {
			return availableVoices[0];
		}
		return null;
	}
}
